import type { GroupBy, GroupProperty } from '../../core/common/types.js';
import type { FilterGroup } from '../../core/filter/types.js';
import type { Sort } from '../../core/sort/types.js';
import { type BasicViewDataType, viewType } from '../../core/view/data-view.js';
import {
  defaultTemporalProfileColumnName,
  resolveTemporalProfileColumn,
} from '../temporal-profile.js';
import { CalendarSingleView } from './calendar-view-manager.js';

export const calendarViewType = viewType('calendar');

export type CalendarMode = 'day' | 'week' | 'month';

export type CalendarViewColumn = {
  id: string;
  hide?: boolean;
  width?: number;
};

export type CalendarIcsSubscription = {
  enabled?: boolean;
  id: string;
  name?: string;
  url: string;
};

type DataType = {
  columns: CalendarViewColumn[];
  filter: FilterGroup;
  groupBy?: GroupBy;
  groupProperties?: GroupProperty[];
  sort?: Sort;
  header?: {
    titleColumn?: string;
    iconColumn?: string;
  };
  startDateCol?: string;
  endDateCol?: string;
  allDayCol?: string;
  timezoneCol?: string;
  recurrenceCol?: string;
  recurrenceExdateCol?: string;
  recurrenceIdCol?: string;
  calendarIdCol?: string;
  locationPhysicalCol?: string;
  locationVirtualCol?: string;
  descriptionCol?: string;
  attendeesCol?: string;
  reminderCol?: string;
  visibilityCol?: string;
  statusCol?: string;
  assigneeCol?: string;
  progressCol?: string;
  dependenciesCol?: string;
  icsSequenceCol?: string;
  icsUidCol?: string;
  invitationMethodCol?: string;
  invitationStateCol?: string;
  sourceMailAccountIdCol?: string;
  sourceMailAttachmentBlobKeyCol?: string;
  sourceMailMessageIdCol?: string;
  sourceTypeCol?: string;
  calendarCursorTime?: number;
  calendarMode?: CalendarMode;
  icsSubscriptions?: CalendarIcsSubscription[];
  visibleCalendarIds?: string[];
};

export type CalendarViewData = BasicViewDataType<
  typeof calendarViewType.type,
  DataType
>;

export const calendarViewModel = calendarViewType.createModel<CalendarViewData>(
  {
    defaultName: 'Calendar View',
    dataViewManager: CalendarSingleView,
    defaultData: viewManager => {
      const columns =
        viewManager.dataSource.getBoundedPropertyIdsWindow().propertyIds;
      const titleColumn = columns.find(
        id => viewManager.dataSource.propertyTypeGet(id) === 'title'
      );
      const dateColumns = columns.filter(
        id => viewManager.dataSource.propertyTypeGet(id) === 'date'
      );
      const checkboxColumns = columns.filter(
        id => viewManager.dataSource.propertyTypeGet(id) === 'checkbox'
      );
      const resolvedStartDateCol =
        resolveTemporalProfileColumn({
          dataSource: viewManager.dataSource,
          role: 'start',
        }) ??
        dateColumns[0] ??
        viewManager.dataSource.propertyAdd('end', {
          type: 'date',
          name: defaultTemporalProfileColumnName('start'),
        });
      const resolvedEndDateCol =
        resolveTemporalProfileColumn({
          dataSource: viewManager.dataSource,
          excludeIds: resolvedStartDateCol ? [resolvedStartDateCol] : [],
          role: 'end',
        }) ??
        dateColumns.find(id => id !== resolvedStartDateCol) ??
        viewManager.dataSource.propertyAdd('end', {
          type: 'date',
          name: defaultTemporalProfileColumnName('end'),
        });
      const viewColumns = [...columns];
      for (const propertyId of [resolvedStartDateCol, resolvedEndDateCol]) {
        if (propertyId && !viewColumns.includes(propertyId)) {
          viewColumns.push(propertyId);
        }
      }
      return {
        columns: viewColumns.map(id => ({ id })),
        filter: {
          type: 'group',
          op: 'and',
          conditions: [],
        },
        header: {
          titleColumn,
          iconColumn: 'type',
        },
        startDateCol: resolvedStartDateCol,
        endDateCol: resolvedEndDateCol,
        allDayCol: checkboxColumns.find(id =>
          /\b(all.?day|ganztag)\b/i.test(
            viewManager.dataSource.propertyNameGet(id)
          )
        ),
        calendarMode: 'week',
      };
    },
  }
);
